import React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  FileText,
  Building2,
  Users,
  CalendarDays,
  CalendarRange,
  StickyNote,
  Wrench,
  DollarSign,
  BarChart3,
  ShieldCheck,
  Landmark,
  PiggyBank,
  Settings as SettingsIcon,
  Bell,
  LogOut,
  Lock,
  UserRound,
  ChevronLeft,
  Menu,
  MoreHorizontal,
  Search,
  UserCircle,
  LifeBuoy,
  School as SchoolIcon,
  Handshake,
  Download,
  Image as ImageIcon,
  Receipt,
  CreditCard,
  Megaphone,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import NotificationBell from "./NotificationBell";
import PushEnableBanner from "@/components/PushEnableBanner";
import InstallAppDialog, { isStandalone } from "@/components/InstallAppDialog";
import AutoInstallPrompt from "@/components/AutoInstallPrompt";
import NotificationSettingsDialog from "@/components/NotificationSettingsDialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/Logo";
import AiAssistant from "@/components/AiAssistant";
import OfflineBar from "@/components/OfflineBar";
import AdminLockScreen, { useAdminLock } from "@/components/AdminLockScreen";
import { toast } from "sonner";
import { fileUrl } from "@/lib/api";

const NAV = [
  { to: "/", label: "Início", icon: Home, exact: true, testid: "nav-inicio", bottom: true },
  { to: "/orcamentos", label: "Orçamentos", icon: FileText, testid: "nav-orcamentos", bottom: true },
  { to: "/escolas", label: "Escolas", icon: Building2, testid: "nav-escolas", bottom: true },
  { to: "/funcionarios", label: "Funcionários", icon: Users, testid: "nav-funcionarios" },
  { to: "/diarias", label: "Diárias / Faltas", icon: CalendarDays, testid: "nav-diarias" },
  { to: "/anotacoes", label: "Anotações", icon: StickyNote, testid: "nav-anotacoes" },
  { to: "/cronograma", label: "Cronograma", icon: CalendarDays, testid: "nav-cronograma", bottom: true },
  { to: "/calendario", label: "Calendário", icon: CalendarRange, testid: "nav-calendario" },
  { to: "/servicos", label: "Serviços", icon: Wrench, testid: "nav-servicos" },
  { to: "/notificacoes", label: "Notificações / Avisos", icon: Megaphone, testid: "nav-notificacoes" },
  { to: "/financeiro", label: "Financeiro", icon: DollarSign, testid: "nav-financeiro" },
  { to: "/financeiro/caixa", label: "Caixa da empresa", icon: ShieldCheck, testid: "nav-caixa" },
  { to: "/financeiro/conta-dia", label: "Conta do dia a dia", icon: Landmark, testid: "nav-conta-dia" },
  { to: "/financeiro/investimento-rafael", label: "Investimento Rafael", icon: PiggyBank, testid: "nav-rafael" },
  { to: "/financeiro/mensal", label: "Consolidado mensal", icon: BarChart3, testid: "nav-mensal" },
  { to: "/financeiro/impostos", label: "Impostos (DAS)", icon: Receipt, testid: "nav-impostos" },
  { to: "/financeiro/cartao", label: "Cartão de crédito", icon: CreditCard, testid: "nav-cartao" },
  { to: "/financeiro/socios", label: "Relatórios dos sócios", icon: Users, testid: "nav-finance-socios" },
  { to: "/financeiro/configuracoes", label: "Config. financeiras", icon: SettingsIcon, testid: "nav-finance-settings" },
  { to: "/relatorios", label: "Relatórios", icon: BarChart3, testid: "nav-relatorios" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, testid: "nav-configuracoes" },
  { to: "/landing-media", label: "Landing (fotos/vídeos)", icon: ImageIcon, testid: "nav-landing-media" },
];

const BOTTOM_NAV = NAV.filter((n) => n.bottom);

const SidebarContent = ({ onNavigate, onLogout }) => (
  <div className="h-full flex flex-col bg-[#1e3a8a] text-white">
    <div className="px-6 pt-6 pb-5 flex justify-center">
      <div className="flex flex-col items-center leading-none">
        <span className="font-heading font-black text-white text-3xl tracking-tight">AMC</span>
        <span className="font-heading font-black text-white text-2xl tracking-tight -mt-1">ESCOLAS</span>
        <div className="h-[2px] w-full bg-red-400 mt-1" />
        <span className="text-[9px] text-white/70 font-bold mt-1.5 tracking-[0.2em] uppercase">
          Manutenção de escolas
        </span>
      </div>
    </div>
    <nav className="flex-1 px-3 py-2 overflow-y-auto space-y-1">
      {NAV.map((n) => (
        <NavLink
          key={n.to + n.label}
          to={n.to}
          end={n.exact}
          onClick={onNavigate}
          data-testid={n.testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive
                ? "bg-white text-[#1e3a8a] shadow-sm"
                : "text-white/85 hover:bg-white/10"
            }`
          }
        >
          <n.icon className="w-5 h-5 shrink-0" strokeWidth={2.2} />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
    <div className="px-3 py-3 border-t border-white/10 space-y-1">
      <a
        href="/?preview=1"
        target="_blank"
        rel="noreferrer"
        data-testid="sidebar-view-landing"
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-white/85 hover:bg-white/10"
      >
        <SchoolIcon className="w-5 h-5 shrink-0" strokeWidth={2.2} />
        <span>Ver página pública</span>
      </a>
      <button
        onClick={onLogout}
        data-testid="sidebar-logout"
        className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-white/85 hover:bg-white/10"
      >
        <LogOut className="w-5 h-5" strokeWidth={2.2} />
        Sair do sistema
      </button>
    </div>
    <div className="px-5 pb-5 pt-3 text-center border-t border-white/10">
      <SchoolIcon className="w-7 h-7 mx-auto text-white/60 mb-2" />
      <p className="text-[11px] text-white/70 leading-tight">
        Cuidamos da sua escola, você cuida do futuro.
      </p>
      <p className="text-[10px] text-white/50 mt-2 font-semibold">AMC Escolas</p>
      <p className="text-[10px] text-white/40">Versão 1.0.0</p>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [financeMenuOpen, setFinanceMenuOpen] = React.useState(false);
  const [sideOpen, setSideOpen] = React.useState(false);
  const [installOpen, setInstallOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const adminLock = useAdminLock();
  const onLock = () => {
    if (!adminLock.configured) { toast.error("Cadastre a senha do bloqueio em Configurações"); navigate("/configuracoes"); return; }
    adminLock.lock();
  };

  const canGoBack = location.pathname !== "/";
  const standalone = isStandalone();
  const mainRef = React.useRef(null);
  React.useEffect(() => { mainRef.current?.scrollTo?.(0, 0); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#f8fafc] max-md:h-[100dvh] max-md:min-h-0 max-md:flex max-md:flex-col max-md:overflow-hidden">
      {adminLock.locked && <AdminLockScreen onUnlocked={adminLock.setUnlocked} />}
      {/* Desktop sidebar */}
      <aside className="w-64 h-screen fixed left-0 top-0 hidden md:flex flex-col z-40">
        <SidebarContent onLogout={logout} />
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-30 shrink-0 bg-white border-b border-slate-200 h-20 px-3 grid grid-cols-[1fr_auto_1fr] items-center">
        <div className="flex items-center">
          {canGoBack ? (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-[#1e3a8a] font-semibold text-base"
              data-testid="mobile-back"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
              Voltar
            </button>
          ) : (
            <Sheet open={sideOpen} onOpenChange={setSideOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6 text-[#1e3a8a]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72 bg-[#1e3a8a] text-white border-none">
                <SidebarContent onNavigate={() => setSideOpen(false)} onLogout={logout} />
              </SheetContent>
            </Sheet>
          )}
        </div>
        <Logo compact center />
        <div className="flex items-center justify-end gap-2">
          {!standalone && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setInstallOpen(true)}
              data-testid="admin-install-btn-mobile"
              className="text-[#1e3a8a] hover:bg-violet-50"
              title="Instalar app"
            >
              <Download className="w-5 h-5" />
            </Button>
          )}
          <NotificationBell compact />
          <UserMenu user={user} onLogout={logout} onLock={onLock} onNotifSettings={() => setNotifOpen(true)} compact />
        </div>
      </header>

      {/* Desktop header */}
      <header className="hidden md:flex md:ml-64 h-20 bg-white border-b border-slate-200 items-center justify-between px-8 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-heading text-xl font-extrabold text-[#1e3a8a] leading-tight">
              AMC Manutenção <span className="text-slate-700 font-semibold">e serviços (sistema)</span>
            </h1>
            <p className="text-xs text-slate-500 leading-tight mt-0.5">
              Cuidamos da sua escola, <span className="text-[#7c3aed] font-semibold">você cuida do futuro.</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!standalone && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setInstallOpen(true)}
              data-testid="admin-install-btn"
              className="h-9 border-slate-200 text-[#1e3a8a] hover:bg-violet-50"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Instalar app
            </Button>
          )}
          <NotificationBell />
          <UserMenu user={user} onLogout={logout} onLock={onLock} onNotifSettings={() => setNotifOpen(true)} />
        </div>
      </header>

      {/* iPhone: o conteúdo rola dentro do <main> e o menu fica no fluxo
          (sem position:fixed), evitando o menu "descolar" no Safari. */}
      <main ref={mainRef} className="md:ml-64 p-4 md:p-8 pb-6 md:pb-10 max-md:flex-1 max-md:min-h-0 max-md:overflow-y-auto max-md:overscroll-contain" data-testid="app-main-scroll">
        <div className="max-w-7xl mx-auto animate-fadeInUp">
          <OfflineBar />
          <div className="mb-4"><PushEnableBanner role="admin" /></div>
          {adminLock.locked ? null : <Outlet />}
        </div>
      </main>

      {/* Mobile bottom nav — 5 slots: Início · Orçamentos · [Financeiro FAB] · Escolas · Anotações
          Financeiro central é um botão destacado que abre um popover flutuante com os 4 sub-itens.
      */}
      <nav
        className="md:hidden relative shrink-0 bg-white border-t border-slate-200 z-40 pb-[env(safe-area-inset-bottom)] mobile-bottom-nav"
        data-testid="mobile-bottom-nav"
      >
        <div className="relative grid grid-cols-5 items-end">
          {[
            NAV.find((n) => n.to === "/"),
            NAV.find((n) => n.to === "/orcamentos"),
          ].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={`bottom-${n.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-[#7c3aed]" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isActive ? "bg-[#7c3aed]/10" : ""}`}>
                    <n.icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  {n.label}
                </>
              )}
            </NavLink>
          ))}

          {/* Financeiro FAB — central, maior, colorido */}
          <div className="relative flex justify-center">
            <button
              type="button"
              onClick={() => setFinanceMenuOpen((o) => !o)}
              data-testid="bottom-financeiro-fab"
              className="absolute -top-6 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-500/30 flex items-center justify-center ring-4 ring-white hover:scale-105 transition-transform"
              aria-label="Financeiro"
              aria-expanded={financeMenuOpen}
            >
              <DollarSign className="w-6 h-6" strokeWidth={2.4} />
            </button>
            <span className="pt-9 pb-2.5 text-[11px] font-semibold text-emerald-700">
              Financeiro
            </span>
          </div>

          {[
            NAV.find((n) => n.to === "/escolas"),
            NAV.find((n) => n.to === "/cronograma"),
          ].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={`bottom-${n.testid}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-[#7c3aed]" : "text-slate-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isActive ? "bg-[#7c3aed]/10" : ""}`}>
                    <n.icon className="w-5 h-5" strokeWidth={2.2} />
                  </div>
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Popover flutuante acima do FAB */}
        {financeMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/20 z-30"
              onClick={() => setFinanceMenuOpen(false)}
              aria-hidden
            />
            <div
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-56 rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden z-40"
              data-testid="finance-menu-popover"
            >
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 px-4 py-2 text-white text-xs font-black uppercase tracking-widest">
                Financeiro
              </div>
              {[
                NAV.find((n) => n.to === "/financeiro"),
                NAV.find((n) => n.to === "/financeiro/caixa"),
                NAV.find((n) => n.to === "/financeiro/conta-dia"),
                NAV.find((n) => n.to === "/financeiro/investimento-rafael"),
                NAV.find((n) => n.to === "/financeiro/mensal"),
                NAV.find((n) => n.to === "/financeiro/impostos"),
                NAV.find((n) => n.to === "/financeiro/cartao"),
                NAV.find((n) => n.to === "/financeiro/socios"),
                NAV.find((n) => n.to === "/diarias"),
                NAV.find((n) => n.to === "/financeiro/configuracoes"),
              ].filter(Boolean).map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setFinanceMenuOpen(false)}
                  data-testid={`finance-menu-${n.testid}`}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-slate-800 border-t border-slate-100 first:border-t-0 hover:bg-emerald-50 active:bg-emerald-100"
                >
                  <n.icon className="w-4 h-4 text-emerald-700 shrink-0" strokeWidth={2.2} />
                  <span className="font-medium">{n.label}</span>
                </Link>
              ))}
            </div>
          </>
        )}
      </nav>

      <AiAssistant />
      <InstallAppDialog open={installOpen} onOpenChange={setInstallOpen} role="admin" />
      <AutoInstallPrompt role="admin" />
      <NotificationSettingsDialog open={notifOpen} onOpenChange={setNotifOpen} />
    </div>
  );
}

const UserMenu = ({ user, onLogout, onLock, onNotifSettings, compact = false }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        className="flex items-center gap-2 rounded-xl hover:bg-slate-100 p-1 pr-2 transition-colors"
        data-testid="user-menu-btn"
      >
        <Avatar className="w-10 h-10 border-2 border-white shadow">
          <AvatarImage src={fileUrl(user?.picture)} alt={user?.name} className="object-cover" />
          <AvatarFallback className="bg-[#1e3a8a] text-white text-sm">
            {user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("") || "U"}
          </AvatarFallback>
        </Avatar>
        {!compact && (
          <div className="hidden md:block text-left pr-1">
            <p className="text-sm font-semibold text-slate-900 leading-tight">{user?.name}</p>
            <p className="text-xs text-slate-500 leading-tight">Administrador</p>
          </div>
        )}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel>Minha conta</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild data-testid="menu-my-account">
        <Link to="/minha-conta"><UserRound className="w-4 h-4 mr-2" />Painel do Adm</Link>
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onLock} data-testid="menu-lock-admin">
        <Lock className="w-4 h-4 mr-2" />
        Bloquear área Adm
      </DropdownMenuItem>
      {onNotifSettings && (
        <DropdownMenuItem onClick={onNotifSettings} data-testid="menu-notif-settings">
          <Bell className="w-4 h-4 mr-2" />
          Notificações
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={onLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-700">
        <LogOut className="w-4 h-4 mr-2" />
        Sair
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
